/* eslint-disable @typescript-eslint/no-explicit-any */
import { SignInProps } from "@/@types";
import { autorun } from "mobx";
import React, { useState, useEffect } from "react";

import ContainerWithSidebar from "@/components/Sidebar";
import { Alert } from "@/components/Modal";
import ReactPhoneInput from "react-phone-input-material-ui";
import { profileSidebarMenuList } from "@/config";
import {
  Box,
  Button,
  Card,
  Container,
  FormHelperText,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { IAlertData } from "@/@types/Alert";
import { UI_ACTION_STATE } from "@/store/common";

const ProfilePage = (props: SignInProps) => {
  const theme = useTheme();

  const [isSubmiting, setIsSubmiting] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [firstname, setFirstname] = useState("");
  const [firstnameError, setFirstnameError] = useState("");
  const [lastname, setLastname] = useState("");
  const [lastnameError, setLastnameError] = useState("");
  const [alertData, setAlertData] = useState<undefined | IAlertData>();

  useEffect(() => {
    const disposeAutorun = autorun(() => {
      setIsSubmiting(true);
      const profile = props.userStore.profile;
      if (
        profile &&
        profile.email &&
        profile.username &&
        profile.phone_number &&
        profile.firstname &&
        profile.lastname &&
        profile.wallet_address
      ) {
        setEmail(profile.email);
        setPhoneNumber(profile.phone_number);
        setFirstname(profile.firstname);
        setLastname(profile.lastname);
      }
      setIsSubmiting(false);
    });
    props.userStore.fetchVerificationDocuments();

    return () => disposeAutorun();
  }, [props.userStore]);
  useEffect(() => {
    props.commonStore.fetchAmlLevels();
    props.commonStore.fetchDocuments();
  }, [props.commonStore]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsSubmiting(true);
    let isError = false;
    if (!email) {
      setEmailError("Please enter your email address.");
      isError = true;
    } else {
      setEmailError("");
    }

    if (!phoneNumber) {
      setPhoneNumberError("Please enter your phone number.");
      isError = true;
    } else {
      setPhoneNumberError("");
    }
    if (!firstname) {
      setFirstnameError("Please enter your first name.");
      isError = true;
    } else {
      setFirstnameError("");
    }
    if (!lastname) {
      setLastnameError("Please enter your last name.");
      isError = true;
    } else {
      setLastnameError("");
    }

    if (isError) {
      setIsSubmiting(false);
      return;
    }
    let _phoneNumber = phoneNumber;
    if (!_phoneNumber.startsWith("+")) {
      setPhoneNumber("+" + _phoneNumber);
      _phoneNumber = "+" + _phoneNumber;
    }

    try {
      props.commonStore.setActionState(UI_ACTION_STATE.PLEASE_WAIT);
      await props.userStore.updateProfile({
        email,
        firstname,
        lastname,
        phone_number: _phoneNumber,
      });
      setAlertData({
        message: "Successfully update profile!",
        closeMethod: () => {
          setAlertData(undefined);
        },
      });
    } catch (e: any) {
      const errorMessage = e.message;
      if (
        errorMessage ===
        'duplicate key value violates unique constraint "users_email_key"'
      ) {
        setEmailError("This email has already been used.");
      } else if (
        errorMessage ===
        'duplicate key value violates unique constraint "users_phone_number_key"'
      ) {
        setPhoneNumberError("This phone number has already been used.");
      } else if (
        errorMessage.includes("value too long for type character varying")
      ) {
        const startIndex = errorMessage.indexOf("(");
        const endIndex = errorMessage.indexOf(")");

        setPhoneNumberError(
          `Please enter a phone number that is no more than ${errorMessage.slice(
            startIndex + 1,
            endIndex,
          )} digits long.(include "+" and country code)`,
        );
      } else {
        setAlertData({
          message: "Update profile failed: " + e.message,
          closeMethod: () => {
            setAlertData(undefined);
          },
        });
      }
    } finally {
      setIsSubmiting(false);
      props.commonStore.setActionState(UI_ACTION_STATE.NONE);
    }
  };

  return (
    <ContainerWithSidebar menu={profileSidebarMenuList}>
      <Container
        maxWidth="lg"
        sx={{
          py: "82px",
          [theme.breakpoints.up("lg")]: { maxWidth: "lg", py: "82px" },
        }}
      >
        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: `${theme.gap}`,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="h2">Your account</Typography>
          <form onSubmit={handleSubmit} className="form">
            <TextField
              label="Email"
              fullWidth
              value={email}
              type="email"
              error={emailError !== ""}
              onChange={(e) => setEmail(e.target.value)}
              helperText={emailError}
              disabled={isSubmiting}
            />

            <TextField
              label="First name"
              fullWidth
              value={firstname}
              type="text"
              error={firstnameError !== ""}
              onChange={(e) => setFirstname(e.target.value)}
              helperText={firstnameError}
              disabled={isSubmiting}
            />
            <TextField
              label="Last name"
              fullWidth
              value={lastname}
              type="text"
              error={lastnameError !== ""}
              onChange={(e) => setLastname(e.target.value)}
              helperText={lastnameError}
              disabled={isSubmiting}
            />
            <ReactPhoneInput
              containerStyle={{
                width: "100%",
              }}
              country={"th"}
              enableAreaCodes={true}
              enableSearch={true}
              autocompleteSearch
              inputStyle={{
                width: "100%",
              }}
              placeholder="Enter phone number"
              value={phoneNumber}
              label="Phone number"
              defaultErrorMessage="Invalid phone number"
              onChange={(e) => setPhoneNumber(e)}
              component={TextField}
            />
            {phoneNumberError && (
              <FormHelperText error={phoneNumberError != ""}>
                {phoneNumberError}
              </FormHelperText>
            )}

            <Box width="fit-content" mx="auto">
              <Button type="submit" variant="contained" size="large">
                Update
              </Button>
            </Box>
          </form>
        </Card>
        {alertData && <Alert alertData={alertData} />}
      </Container>
    </ContainerWithSidebar>
  );
};

export default ProfilePage;
